import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Users, FolderKanban, Receipt, Inbox, ScrollText, ArrowLeft, ShieldAlert, BadgeIndianRupee, Globe, Wallet, Smartphone } from "lucide-react";
import { Logo } from "./brand/Logo";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import RechargeBanner from "./admin/credits/RechargeBanner";

const NAV = [
  ["/admin", "Overview", LayoutDashboard, true],
  ["/admin/users", "Users", Users],
  ["/admin/projects", "Projects", FolderKanban],
  ["/admin/credits", "Credits", Wallet],
  ["/admin/pricing", "Pricing", BadgeIndianRupee],
  ["/admin/orders", "Payments", Receipt],
  ["/admin/domains", "Domains", Globe],
  ["/admin/mobile-app", "Mobile app", Smartphone],
  ["/admin/contact", "Contact inbox", Inbox],
  ["/admin/audit-log", "Audit log", ScrollText],
];

const NoAccess = () => {
  const { refresh } = useAuth();
  const [canBootstrap, setCanBootstrap] = useState(false);
  useEffect(() => { api.adminBootstrapStatus().then((r) => setCanBootstrap(!r.admin_exists)).catch(() => {}); }, []);
  const claim = async () => { try { await api.adminBootstrap(); await refresh(); toast.success("You're now the admin"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#F5F8FC] text-center px-6" data-testid="admin-no-access">
      <ShieldAlert className="text-amber-600" size={36} />
      <h1 className="text-2xl font-bold text-slate-900">Admin access required</h1>
      {canBootstrap && <button onClick={claim} data-testid="admin-bootstrap-btn" className="h-10 px-5 rounded-lg bg-slate-900 text-white text-sm font-semibold">Become the first admin</button>}
      <Link to="/home" className="text-sm text-emerald-600">Back to projects</Link>
    </div>
  );
};

const AdminLayout = () => {
  const { user } = useAuth();
  if (!user?.is_admin) return <NoAccess />;
  return (
    <div className="min-h-screen flex bg-[#F5F8FC]" data-testid="admin-layout">
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-slate-200 bg-slate-50 p-4">
        <Logo to="/admin" />
        <span className="mt-1 ml-10 text-[10px] font-mono uppercase tracking-wider text-amber-600">Admin</span>
        <nav className="mt-8 space-y-1 flex-1">
          {NAV.map(([to, label, Icon, end]) => (
            <NavLink key={to} to={to} end={end} data-testid={`admin-nav-${label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) => `flex items-center gap-2.5 h-9 px-3 rounded-lg text-sm transition-colors ${isActive ? "bg-slate-100 text-slate-900" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"}`}>
              <Icon size={15} /> {label}
            </NavLink>
          ))}
        </nav>
        <Link to="/home" className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900" data-testid="admin-back-app"><ArrowLeft size={13} /> Back to app</Link>
      </aside>
      <div className="flex-1 min-w-0">
        <div className="md:hidden flex gap-1 overflow-x-auto scrollbar-hide border-b border-slate-200 p-2">
          {NAV.map(([to, label, , end]) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => `shrink-0 h-8 px-3 rounded-md text-xs flex items-center ${isActive ? "bg-slate-100 text-slate-900" : "text-slate-500"}`}>{label}</NavLink>
          ))}
        </div>
        <main className="p-4 sm:p-6 lg:p-8"><RechargeBanner /><Outlet /></main>
      </div>
    </div>
  );
};

export default AdminLayout;
